import {
    ActionReducerMapBuilder,
    createAsyncThunk,
    createSlice,
    Draft,
    SerializedError,
} from '@reduxjs/toolkit';
import { urlUserProjects } from '../../Constants';
import { IAction, SlicePropsWithData } from '../store';
import { apiFetch } from '../../util/ProjectUtils';

/**
 * Enum for the style in which projects should be presented to the user.
 */
export enum ProjectDisplayType {
    Tile,
    Tree,
    List,
}

/**
 * Enum for the type of project view to show the user.
 */
export enum ProjectViewType {
    All,
    Favourites,
    Recent,
}

/**
 * Initial slice state value.
 */
const initialState: IUserProjectsState = {
    data: [],
    loading: true,
    error: null,
    settings: {
        sidebarOpen: false,
        displayType: ProjectDisplayType.Tile,
        viewType: ProjectViewType.All,
        openNodes: [],
        activeCategory: '',
        filter: '',
        sortAz: true,
        filterVisible: false,
    },
};

/**
 * Collection of the reducers for this slice.
 */
const reducers = {
    /**
     * Updates the active display type.
     * @param state - Slice state object.
     * @param action - Action to execute against the state.
     */
    setDisplayType(state: IUserProjectsState, action: IAction<ProjectDisplayType>) {
        state.settings.displayType = action.payload;
    },

    /**
     * Updates the active view type.
     * @param state - Slice state object.
     * @param action - Action to execute against the state.
     */
    setViewType(state: IUserProjectsState, action: IAction<ProjectViewType>) {
        state.settings.viewType = action.payload;
    },

    /**
     * Updates the active project category.
     * @param state - Slice state object.
     * @param action - Action to execute against the state.
     */
    setActiveCategory(state: IUserProjectsState, action: IAction<string>) {
        state.settings.activeCategory = action.payload;
    },

    /**
     * Updates the project filter value.
     * @param state - Slice state object.
     * @param action - Action to execute against the state.
     */
    setFilter(state: IUserProjectsState, action: IAction<string>) {
        state.settings.filter = action.payload;
    },

    /**
     * Updates the project sorting direction.
     * @param state - Slice state object.
     * @param action - Action to execute against the state.
     */
    setSortAz(state: IUserProjectsState, action: IAction<boolean>) {
        state.settings.sortAz = action.payload;
    },

    /**
     * Updates the project sidebar open state.
     * @param state - Slice state object.
     * @param action - Action to execute against the state.
     */
    setSidebarOpen(state: IUserProjectsState, action: IAction<boolean>) {
        state.settings.sidebarOpen = action.payload;
    },

    /**
     * Updates the project filter visibility.
     * @param state - Slice state object.
     * @param action - Action to execute against the state.
     */
    setFilterVisible(state: IUserProjectsState, action: IAction<boolean>) {
        state.settings.filterVisible = action.payload;
    },

    /**
     * Updates the list of open project TreeView nodes.
     * @param state - Slice state object.
     * @param action - Action to execute against the state.
     */
    setOpenNodes(state: IUserProjectsState, action: IAction<string[]>) {
        state.settings.openNodes = action.payload;
    },
};

/**
 * Collection of the thunks for this slice, along with their reducer functions.
 */
const thunks = {
    /**
     * Fetches the user's project list from the backend.
     */
    getProjects: async (): Promise<IEplassProjectData[]> => {
        try {
            const response = await apiFetch(urlUserProjects);

            return Promise.resolve(await response.json());
        } catch (e) {
            return Promise.reject(e as Error);
        }
    },

    /**
     * Registers the 'extraReducers' functions for the slice's thunks.
     */
    _reducers: (builder: ActionReducerMapBuilder<IUserProjectsState>) => {
        /**
         * Reducers for the 'getSettings' thunk.
         */

        builder.addCase(getProjects.pending, (state: Draft<IUserProjectsState>) => {
            state.loading = true;
        });
        builder.addCase(
            getProjects.fulfilled,
            (state: Draft<IUserProjectsState>, action: IAction<IEplassProjectData[]>) => {
                state.loading = false;
                state.data = action.payload?.map(x => {
                    /**
                     * Add a trailing slash to the project's category to prevent categories with paths that start similarly
                     * being accidentally selected (e.g. 'Test - one' and 'Test').
                     */
                    x.category += '\\';

                    return x;
                });
            },
        );
        builder.addCase(
            getProjects.rejected,
            (state: Draft<IUserProjectsState>, action: IAction<SerializedError>) => {
                state.loading = false;
                state.error = action.error;
            },
        );
    },
};

/**
 * Expose thunk functions as exports.
 */
export const getProjects = createAsyncThunk('user/projects', thunks.getProjects);

/**
 * Interface that describes the state object for this redux slice.
 */
export interface IUserProjectsState extends SlicePropsWithData<IEplassProjectData[]> {
    /** User project settings. */
    settings: IProjectSettingsState;
}

/**
 * Interface that describes the possible settings for the user's project data.
 */
export interface IProjectSettingsState {
    /** If the projects sidebar is open or not. */
    sidebarOpen: boolean;

    /** Project display type (Tree/Tile/Row). */
    displayType: ProjectDisplayType;

    /** Project view type (All/Favourites/Recent). */
    viewType: ProjectViewType;

    /** User-selected project category. */
    activeCategory: string;

    /** Collection of open TreeView nodes. */
    openNodes: string[];

    /** User project name filter. */
    filter: string;

    /** Project sorting direction. */
    sortAz: boolean;

    /** If the filter input is visible or not. */
    filterVisible: boolean;
}

/**
 * Interface that describes the properties of an Eplass project.
 */
export interface IEplassProjectData {
    /** Project category. */
    category: string;

    /** Project host server. */
    host: string;

    /** Project Notes server. */
    notesServer: string;

    /** Project ReplicaId. */
    replicaId: string;

    /** Project Type. */
    type: string;

    /** Project Title. */
    title: string;

    /** Project module data. */
    module: IUserProjectModuleData;
}

/**
 * Interface that describes the props for IUserProjectModuleData.
 */
export interface IUserProjectModuleData {
    moduleAbbreviation: string;
    tagColor: string;
    translations: Record<string, string>;
}

/**
 * Create the redux slice.
 */
export const projectsSlice = createSlice({
    name: 'projects',
    extraReducers: thunks._reducers,
    initialState,
    reducers,
});

/**
 * Export functions from slice actions.
 */
export const {
    setDisplayType,
    setViewType,
    setActiveCategory,
    setFilter,
    setSortAz,
    setFilterVisible,
    setSidebarOpen,
    setOpenNodes,
} = projectsSlice.actions;

/**
 * Export slice reducer as default.
 */
export default projectsSlice.reducer;
